/** Los comentarios al pie de una página: cualquiera con acceso a ella puede escribir, con fotos,
 * archivos y menciones. Cada cual edita lo suyo; administración puede borrar, no reescribir. */
import { Avatar } from '../componentes/piezas'
import { useCallback, useEffect, useState } from 'react'

import { ErrorDeApi, api } from '../api'
import { ListaDeAdjuntos } from '../componentes/adjuntos'
import { CajaDeComentario, TextoConMenciones, aEditable } from '../componentes/menciones'
import { usePortal } from '../contexto'
import { hace } from '../textos'
import type { Adjunto, Persona } from '../tipos'

interface Comentario {
  id: number
  autor: Persona | null
  body: string
  created_at: string
  edited_at: string | null
  puede_editar: boolean
  puede_borrar: boolean
  adjuntos: Adjunto[]
  menciones: Record<string, string>
}

export default function Comentarios({ paginaId, rutaPaginas }: { paginaId: number; rutaPaginas: string }) {
  const { avisar, ruta } = usePortal()
  const base = `${rutaPaginas}/${paginaId}/comentarios`
  const [lista, setLista] = useState<Comentario[] | null>(null)
  const [editando, setEditando] = useState<number | null>(null)

  const fallo = useCallback(
    (e: unknown, defecto: string) => avisar(e instanceof ErrorDeApi ? e.message : defecto, 'malo'),
    [avisar],
  )

  useEffect(() => {
    let vivo = true
    setLista(null)
    api.get<Comentario[]>(base).then((l) => vivo && setLista(l)).catch(() => vivo && setLista([]))
    return () => { vivo = false }
  }, [base])

  const candidatos = useCallback(
    () => api.get<Persona[]>(`${rutaPaginas}/${paginaId}/mencionables`),
    [rutaPaginas, paginaId],
  )

  const borrar = async (id: number) => {
    try {
      await api.borrar(`${base}/${id}`)
      setLista((l) => (l ?? []).filter((c) => c.id !== id))
    } catch (e) {
      fallo(e, 'No se ha podido borrar el comentario.')
    }
  }

  return (
    <section className="comentarios" aria-label="Comentarios">
      <h3 className="comentarios__titulo">
        Comentarios{lista && lista.length > 0 ? ` · ${lista.length}` : ''}
      </h3>

      {lista?.map((c) => (
        <article className="comentario-p" key={c.id}>
          <Avatar nombre={c.autor?.nombre ?? '?'} />
          <div className="comentario-p__cuerpo">
            <div className="comentario-p__cabecera">
              <strong>{c.autor?.nombre ?? 'Alguien que ya no está'}</strong>
              <span>{hace(c.created_at)}{c.edited_at ? ' · editado' : ''}</span>
              <span className="comentario-p__acciones">
                {c.puede_editar && editando !== c.id && (
                  <button className="arbol-boton arbol-boton--visible" aria-label="Editar comentario"
                          onClick={() => setEditando(c.id)}>✎</button>
                )}
                {c.puede_borrar && (
                  <button className="arbol-boton arbol-boton--visible" aria-label="Borrar comentario"
                          onClick={() => void borrar(c.id)}>🗑</button>
                )}
              </span>
            </div>
            {editando === c.id ? (
              <div className="comentario-p__edicion">
                <CajaDeComentario
                  rutaArchivos={`${ruta}/archivos`}
                  cargarCandidatos={candidatos}
                  inicial={aEditable(c.body, c.menciones)}
                  sinArchivos
                  autoFocus
                  textoBoton="Guardar"
                  alCancelar={() => setEditando(null)}
                  alEnviar={async ({ cuerpo }) => {
                    const nuevo = await api.patch<Comentario>(`${base}/${c.id}`, { body: cuerpo })
                    setLista((l) => (l ?? []).map((x) => (x.id === c.id ? nuevo : x)))
                    setEditando(null)
                  }}
                />
              </div>
            ) : (
              <>
                {c.body && <TextoConMenciones cuerpo={c.body} menciones={c.menciones} />}
                <ListaDeAdjuntos adjuntos={c.adjuntos} />
              </>
            )}
          </div>
        </article>
      ))}

      <div className="comentarios__nuevo">
        <CajaDeComentario
          rutaArchivos={`${ruta}/archivos`}
          camposDeSubida={{ pagina: String(paginaId), comentario: 'true' }}
          cargarCandidatos={candidatos}
          alEnviar={async ({ cuerpo, adjuntos }) => {
            const nuevo = await api.post<Comentario>(base, { body: cuerpo, adjuntos })
            setLista((l) => [...(l ?? []), nuevo])
          }}
        />
      </div>
    </section>
  )
}
